import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  Button,
  Spin
} from 'antd';
import {
  createNewLeadAtom,
  myLeadsHeaderAtom,
  reloadLeadsAtom,
  userAtom,
} from '../../../../atoms/Atoms';
import MainPage from '../../../shared-components/main-page/MainPage';
import { H1 } from '../../../shared-components/typography/Title';
import {
  percentage,
  thousand,
  toCurrency,
} from '../../../../functions';
import CreateNewLeadModal from './shared-components/CreateNewLeadModal';
import Card from '../../../shared-components/container/Card';
import LeadsPending from './shared-components/LeadsPending';
import LeadsOpen from './shared-components/LeadsOpen';
import LeadsClosed from './shared-components/LeadsClosed';
import LeadsLost from './shared-components/LeadsLost';
import LeadsSearchTable from './shared-components/LeadsSearchTable';
import api from '../../../../api/api';

function Leads({ includeBack }) {
  const { t } = useTranslation();
  const history = useHistory();
  const myLeadsHeader = useRecoilValue(myLeadsHeaderAtom);
  const [createNewLead, setCreateNewLead] = useRecoilState(createNewLeadAtom);
  const reloadLeads = useRecoilValue(reloadLeadsAtom);
  const [selectedTab, setSelectedTab] = useState(1);
  const [loadingTargetData, setLoadingTargetData] = useState(null);
  const [targetData, setTargetData] = useState(null);
  const user = useRecoilValue(userAtom);

  function getTargetData() {
    setLoadingTargetData(true);
    api.get('leads/targets/me?type=0')
    .then((response) => {
      setLoadingTargetData(false);
      if (response.data.success) {
        setTargetData(response.data.data);
      }
    })
    .catch((error) => {
      setLoadingTargetData(false);
    })
  }

  useEffect(() => {
    if (reloadLeads) {
      getTargetData();
    }
  }, [reloadLeads]);

  useEffect(() => {
    getTargetData();
  }, []);

  return (
    <MainPage includeBack requiresCloser>
      <div className="md:flex mt-4 space-y-2 space-x-2">
        <H1 text={t('my_opportunities')} className="mr-auto" />
        <Button
          className="ml-auto w-full md:w-auto"
          type="primary"
          onClick={() => history.push('/dashboard/me')}
        >
          {t('dashboard')}
        </Button>
        <Button
          type="primary"
          className="ml-auto"
          onClick={() => setCreateNewLead({
            ...createNewLead,
            open: true
          })}
        >
          {t('create_new')}
        </Button>
      </div>
      <div className="grid gap-4 mt-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <div className="col-span-1">
          <Spin spinning={loadingTargetData}>
            <Card
              className="w-full rounded-sm"
              textPosition="center"
              title={t('monthly_target')}
              description={`${thousand(targetData?.closerMonthlySignedDeals)}/${thousand(user?.closerMonthlySignedDealsTarget)}`}
              descriptionSize="text-2xl"
            >
              {percentage((targetData?.closerMonthlySignedDeals / user?.closerMonthlySignedDealsTarget) * 100, 0)}
            </Card>
          </Spin>
        </div>
        <div className="col-span-1">
          <Spin spinning={loadingTargetData}>
            <Card
              className="w-full rounded-sm"
              textPosition="center"
              title={t('yearly_target')}
              description={`${thousand(targetData?.closerYearlySignedDeals)}/${thousand(user?.closerYearlySignedDealsTarget)}`}
              descriptionSize="text-2xl"
            >
              {percentage((targetData?.closerYearlySignedDeals / user?.closerYearlySignedDealsTarget) * 100, 0)}
            </Card>
          </Spin>
        </div>
        <div className="col-span-1">
          <Spin spinning={loadingTargetData}>
            <Card
              className="w-full rounded-sm"
              textPosition="center"
              title={t('total_kw')}
              description={thousand(targetData?.totalKw / 1000)}
              descriptionSize="text-2xl"
            />
          </Spin>
        </div>
        <div className="col-span-1">
          <Spin spinning={loadingTargetData}>
            <Card
              className="w-full rounded-sm"
              textPosition="center"
              title={t('total_epc')}
              description={toCurrency(targetData?.totalEpc)}
              descriptionSize="text-2xl"
            />
          </Spin>
        </div>
      </div>
      <div className="grid gap-4 mt-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-5">
        <div className="col-span-1 md:col-span-2 lg:col-span-1 ">
          <Spin spinning={myLeadsHeader?.loading}>
            <button
              aria-label={t('all_opportunities')}
              type="button"
              className={`w-full border-4 rounded-sm ${selectedTab === -1 ? 'border-primaryColor' : 'border-transparent'}`}
              onClick={() => setSelectedTab(-1)}
            >
              <Card
                title={t('all_opportunities')}
                description={thousand((myLeadsHeader?.totalPendingAssigned +
                                       myLeadsHeader?.totalPendingNoAppointment +
                                       myLeadsHeader?.totalAssigned +
                                       myLeadsHeader?.totalRevisit +
                                       myLeadsHeader?.totalFailedCredit +
                                       myLeadsHeader?.totalInstalled +
                                       myLeadsHeader?.totalActivated +
                                       myLeadsHeader?.totalLost))}
                fakeChildren
              />
            </button>
          </Spin>
        </div>
        <Spin spinning={myLeadsHeader?.loading}>
          <button
            aria-label={t('leads')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 0 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(0)}
          >
            <Card
              title={t('leads')}
              description={thousand((myLeadsHeader?.totalPendingAssigned + myLeadsHeader?.totalPendingNoAppointment))}
              fakeChildren
            />
          </button>
        </Spin>
        <Spin spinning={myLeadsHeader?.loading}>
          <button
            aria-label={t('deals')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 1 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(1)}
          >
            <Card
              title={t('deals')}
              description={thousand((myLeadsHeader?.totalAssigned + myLeadsHeader?.totalRevisit + myLeadsHeader?.totalFailedCredit))}
            >
              <p className="text-right text-xs"><strong>{t('waiting_signature')}:</strong> {thousand(myLeadsHeader.waitingSignatureKw / 1000)}kw ({thousand(myLeadsHeader.waitingSignatureCount)})</p>
              <p className="text-right text-xs"><strong>{t('pending_site_survey')}:</strong> {thousand(myLeadsHeader.pendingSurveyedKw / 1000)}kw ({thousand(myLeadsHeader.pendingSurveyedCount)})</p>
              <p className="text-right text-xs"><strong>{t('site_survey_completed')}:</strong> {thousand(myLeadsHeader.siteSurveyedKw / 1000)}kw ({thousand(myLeadsHeader.siteSurveyedCount)})</p>
              <p className="text-right text-xs"><strong>{t('pending_install')}:</strong> {thousand(myLeadsHeader.pendingInstallKw / 1000)}kw ({thousand(myLeadsHeader.pendingInstallCount)})</p>
            </Card>
          </button>
        </Spin>
        <Spin spinning={myLeadsHeader?.loading}>
          <button
            aria-label={t('installs')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 2 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(2)}
          >
            <Card
              title={t('installs')}
              description={thousand((myLeadsHeader?.totalInstalled + myLeadsHeader?.totalActivated))}
            >
              <p className="text-right text-xs"><strong>{t('installed')}:</strong> {thousand(myLeadsHeader.installedKw / 1000)}kw ({thousand(myLeadsHeader.installedCount)})</p>
              <p className="text-right text-xs"><strong>{t('PTO')}:</strong> {thousand(myLeadsHeader.activatedKw / 1000)}kw ({thousand(myLeadsHeader.totalActivated)})</p>
            </Card>
          </button>
        </Spin>
        <Spin spinning={myLeadsHeader?.loading}>
          <button
            aria-label={t('lost')}
            type="button"
            className={`col-span-1 w-full border-4 rounded-sm ${selectedTab === 3 ? 'border-primaryColor' : 'border-transparent'}`}
            onClick={() => setSelectedTab(3)}
          >
            <Card
              title={t('lost')}
              description={thousand(myLeadsHeader?.totalLost)}
              fakeChildren           
            />
          </button>
        </Spin>
      </div>
      {selectedTab === -1 && (
        <LeadsSearchTable searchType={0} className="mt-2" />
      )}
      {selectedTab === 0 && (
        <LeadsPending className="mt-2" />
      )}
      {selectedTab === 1 && (
        <LeadsOpen className="mt-2" />
      )}
      {selectedTab === 2 && (
        <LeadsClosed className="mt-2" />
      )}
      {selectedTab === 3 && (
        <LeadsLost className="mt-2" />
      )}
      <CreateNewLeadModal />
    </MainPage>
  );
}

export default Leads;
